import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import qs from 'qs';
import { LoadingBar } from 'quasar';
import router from '@/router/router';
import { useAuthStore } from '@/store/auth';
import { isBlank } from '@/utils/lang.ts';
import { camelToSnakeCase, snakeToCamelCase } from '@/utils/object';

axios.interceptors.request.use((config): InternalAxiosRequestConfig<any> => {
  LoadingBar.start();
  return config;
});

axios.interceptors.response.use(
  (response: AxiosResponse) => {
    LoadingBar.stop();
    return response;
  },
  (error: AxiosError) => {
    LoadingBar.stop();
    return Promise.reject(error);
  }
);

export const backendAPI = (
  method: string,
  path: string,
  data: any = {},
  headers: any = {},
  responseType: any = null,
  withCredentials = false,
  options: any = {}
): Promise<AxiosResponse> => {
  return new Promise<AxiosResponse>((resolve, reject) => {
    const opts = optionBuilder(options);
    const processedData = opts.isKeepRawData ? data : camelToSnakeCase(data);
    requester(method, path, processedData, headers, responseType, withCredentials, opts.isMultipartFormData)
      .then((resp: AxiosResponse) => {
        if (opts.isKeepRawResponseData) {
          resolve(resp);
        }
        if (resp.status === 204) {
          resolve({ ...resp, data: {} });
        } else if (resp.status === 200 || resp.status === 201 || resp.status === 202) {
          resolve(snakeToCamelCase(resp));
        }
      })
      .catch((error: any) => {
        if (error.response?.status === 401) {
          const authStore = useAuthStore();
          const listRoutes = ['Login', 'ForgotPassword', 'ResetPassword'];
          authStore.logout();
          if (!listRoutes.includes(router.currentRoute.value.name as string)) {
            router.push({ name: 'Login' });
          }
        }
        // Reject with parsed data if available, otherwise with the original error
        reject(axiosErrorParse(error) ?? error);
      });
  });
};

// PRIVATE

const optionBuilder = (options: any) => {
  return {
    isKeepRawData: false,
    isMultipartFormData: false,
    ...options,
  };
};
const axiosErrorParse = (error: AxiosError) => {
  const data = error.response?.data;
  const detail = (data as any)?.detail;
  const isStructuredDetail =
    typeof detail === 'object' && detail !== null && !Array.isArray(detail) && 'error_code' in detail;
  return {
    ...(typeof data === 'object' && data !== null ? data : {}),
    error_code: isStructuredDetail ? detail.error_code : undefined,
    detail: isStructuredDetail ? detail.message : detail,
    status: error.response?.status,
  };
};

const requester = (
  method: string,
  path: string,
  data: any = {},
  headers: any = {},
  responseType: any = null,
  withCredentials = false,
  isMultipartFormData = false
): Promise<AxiosResponse> => {
  // Attach tokens to headers
  headers = attachBearToken(headers);

  const axiosDefaultSetting = defaultAxiosDefaultSetting(method, path, data, headers, responseType, withCredentials, isMultipartFormData);

  return axios(axiosDefaultSetting);
};

const attachBearToken = (headers: any): any => {
  const userStore = useAuthStore();
  const user = userStore.getCurrentUser;
  const userToken = user?.accessToken;
  if (isBlank(userToken)) return headers;
  if (headers.attachUserToken === false) return headers;

  return {
    ...headers,
    Authorization: `Bearer ${userToken}`,
  };
};

const defaultAxiosDefaultSetting = (
  method: string,
  path: string,
  data: any = {},
  headers: any = {},
  responseType: any = null,
  withCredentials = false,
  isMultipartFormData = false
): AxiosRequestConfig => {
  const axiosSetting: AxiosRequestConfig = {
    method: method,
    url: import.meta.env.VITE_APP_BACKEND_END_POINT + path,
    withCredentials: withCredentials,
    headers: {
      Accept: 'application/json',
      // Don't set Content-Type for multipart - axios sets it automatically with boundary
      ...(isMultipartFormData ? {} : { 'Content-Type': 'application/json' }),
      ...headers,
    },
  };

  // Set request parameter or body
  if (method.toLowerCase() == 'get') {
    axiosSetting.params = data;
    axiosSetting.paramsSerializer = (params) => {
      return qs.stringify(params);
    };
  } else {
    axiosSetting.data = data;
  }

  if (responseType) {
    axiosSetting.responseType = responseType;
  }

  return axiosSetting;
};
